/**
 * i18n 工具函数
 */

import { i18nStore } from "@/store/i18n";
import { locales } from "./locales";
import type { TranslationDict, TranslationValue } from "./types";
import { DEFAULT_LOCALE } from "./types";

/**
 * 从嵌套对象中获取值
 * 支持点号分隔的路径，如 'model.image.Seedream 4.0.label'
 *
 * @param obj - 要搜索的对象
 * @param path - 点号分隔的路径
 * @returns 找到的值，如果没找到则返回 undefined
 */
function getNestedValue(
  obj: TranslationDict,
  path: string | string[]
): TranslationValue | TranslationDict | undefined {
  const keys = Array.isArray(path) ? path : path.split(".");
  let current: TranslationValue | TranslationDict | undefined = obj;

  for (const key of keys) {
    if (current && typeof current === "object" && !Array.isArray(current)) {
      current = (current as TranslationDict)[key];
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * 获取翻译文本
 *
 * 支持两种调用方式：
 * 1. t(key) - 使用全局 i18nStore.locale
 * 2. t(key, params) - 使用全局 locale + 参数
 * 3. t(key, locale) - 手动指定 locale（不推荐，仅用于特殊场景）
 * 4. t(key, locale, params) - 完整参数（不推荐，仅用于特殊场景）
 *
 * @param key - 翻译键，支持点号分隔的路径
 * @param localeOrParams - 语言代码或参数对象（可选）
 * @param params - 可选的参数，用于动态翻译（当第二个参数是 locale 时使用）
 * @returns 翻译后的文本，如果找不到则返回 key 本身
 */
export function t(key: string | string[], defaultValue?: string): string {
  // 判断第二个参数是 locale 还是 params
  const locale = i18nStore.locale;
  let translationParams: Record<string, unknown> | undefined;

  const value =
    getNestedValue(locales[locale], key) ??
    getNestedValue(locales[DEFAULT_LOCALE], key);

  // 如果找到的是函数，调用它
  if (typeof value === "function") {
    return value(translationParams);
  }

  // 如果找到的是字符串，直接返回
  if (typeof value === "string") {
    return value;
  }

  // 都找不到，返回 key 本身
  return defaultValue ?? (Array.isArray(key) ? key.join(".") : key);
}
